import React, { memo, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const MainLabel = styled.span`
  color: ${props => props.$color || '#FFF'};
  font-family: 'Generic-G80-RD-Standard-DEMO', 'PP Mori', -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 2%;
  white-space: nowrap;
  cursor: url('/icons/pointer.svg') 10 0, pointer;
  pointer-events: auto;
  user-select: none;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 22px;
  }
`;

const ToggleButton = styled.button`
  height: 40px;
  background: transparent;
  border: none;
  cursor: url('/icons/pointer.svg') 10 0, pointer;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  outline: none;
  pointer-events: auto;
  user-select: none;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ToggleTrack = styled.div`
  height: 40px;
  background: ${props => props.$backgroundColor || '#FFFFFF'};
  border-radius: 20px;
  position: relative;
  transition: background-color 0.3s ease;
  padding: 7px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ToggleThumb = styled.div`
  height: 26px;
  background: #000000;
  border-radius: 13px;
  position: absolute;
  top: 7px;
  left: 7px;
  transition: transform 0.3s ease;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: transparent; /* keep width influenced by label without showing text */
  font-family: 'Generic-G80-RD-Standard-DEMO', -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  white-space: nowrap;
  transform: ${props => props.$isActive ? `translateX(${props.$translateX || 0}px)` : 'translateX(0)'};
`;

const ToggleTextLeft = styled.div`
  color: ${props => props.$isActive ? 'rgba(0, 0, 0, 0.5)' : '#FFFFFF'};
  font-family: 'Generic-G80-RD-Standard-DEMO', -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  white-space: nowrap;
  transition: color 0.3s ease;
  pointer-events: none;
  z-index: 1;
  padding: 0 8px;
  display: flex;
  align-items: center;
  height: 26px;
`;

const ToggleTextRight = styled.div`
  color: ${props => props.$isActive ? '#FFFFFF' : 'rgba(0, 0, 0, 0.5)'};
  font-family: 'Generic-G80-RD-Standard-DEMO', -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  white-space: nowrap;
  transition: color 0.3s ease;
  pointer-events: none;
  z-index: 1;
  padding: 0 8px;
  display: flex;
  align-items: center;
  height: 26px;
`;

const LabelledToggle = memo(({ isActive, onToggle, onLabel = 'ON', offLabel = 'OFF', ariaLabel, label, labelColor, backgroundColor = 'white' }) => {
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const [translateX, setTranslateX] = useState(0);
  const prevIsActiveRef = useRef(isActive);
  const rafIdRef = useRef(null);

  useLayoutEffect(() => {
    // Cancel any pending RAF to avoid race conditions
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    const compute = () => {
      if (!trackRef.current || !thumbRef.current) return;
      
      const trackWidth = trackRef.current.clientWidth;
      const horizontalPadding = 14; // track padding: 7px left + 7px right
      
      // Check if isActive has changed - if so, we need to wait for content update
      const hasStateChanged = prevIsActiveRef.current !== isActive;
      
      if (hasStateChanged) {
        // State changed - use requestAnimationFrame to ensure DOM has updated
        // Double RAF ensures we're measuring after the browser has painted the new content
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = requestAnimationFrame(() => {
            if (!trackRef.current || !thumbRef.current) return;
            // Force reflow to ensure accurate measurement
            void thumbRef.current.offsetHeight;
            const thumbWidth = thumbRef.current.clientWidth;
            const next = Math.max(0, trackWidth - thumbWidth - horizontalPadding);
            setTranslateX(next);
            prevIsActiveRef.current = isActive;
            rafIdRef.current = null;
          });
        });
      } else {
        // No state change (resize or label change) - measure immediately
        void thumbRef.current.offsetHeight;
        const thumbWidth = thumbRef.current.clientWidth;
        const next = Math.max(0, trackWidth - thumbWidth - horizontalPadding);
        setTranslateX(next);
      }
    };

    compute();

    window.addEventListener('resize', compute);
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      window.removeEventListener('resize', compute);
    };
  }, [isActive, onLabel, offLabel]);

  return (
    <Container>
      {label ? (
        <MainLabel 
          $color={labelColor} 
          onClick={onToggle}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle();
            }
          }}
          aria-label={ariaLabel || `Toggle ${onLabel}/${offLabel}`}
        >
          {label}
        </MainLabel>
      ) : null}
      <ToggleButton onClick={onToggle} aria-label={ariaLabel || `Toggle ${onLabel}/${offLabel}`}>
        <ToggleTrack ref={trackRef} $backgroundColor={backgroundColor} $isActive={isActive}>
          <ToggleTextLeft $isActive={isActive}>
            {offLabel}
          </ToggleTextLeft>
          <ToggleTextRight $isActive={isActive}>
            {onLabel}
          </ToggleTextRight>
          <ToggleThumb ref={thumbRef} $isActive={isActive} $translateX={translateX}>
            {isActive ? onLabel : offLabel}
          </ToggleThumb>
        </ToggleTrack>
      </ToggleButton>
    </Container>
  );
});

LabelledToggle.displayName = 'LabelledToggle';

export default LabelledToggle;


